import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, Check, GitFork, Pause, Play, RotateCcw, ShieldAlert, Sparkles, X, Zap, FlaskConical,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useResearch } from "@/store/researchStore";
import { useDeepResearch } from "@/hooks/useDeepResearch";
import { useAgentSwarm } from "@/hooks/useAgentSwarm";
import { DeepResearchGraph, SwarmAgentList } from "./research/DeepResearchGraph";
import { ResearchAuditPanel } from "./research/ResearchAuditPanel";

export function TransparencyPanel() {
  // Research-aware state
  const enabled = useResearch((s) => s.enabled);
  const status = useResearch((s) => s.status);
  const metrics = useResearch((s) => s.metrics);
  const guardrails = useResearch((s) => s.guardrails);
  const setGuardrails = useResearch((s) => s.setGuardrails);
  const { pause, resume, rollback, stop, isPaused, isRunning } = useDeepResearch();
  const { agents, plan } = useAgentSwarm();

  // Legacy demo (kept as fallback when no research run has ever started)
  const [confidence, setConfidence] = useState(94);
  const [risk, setRisk] = useState(12);
  useEffect(() => {
    if (status !== "idle") return;
    const id = setInterval(() => {
      setConfidence(85 + Math.floor(Math.random() * 12));
      setRisk(8 + Math.floor(Math.random() * 18));
    }, 2400);
    return () => clearInterval(id);
  }, [status]);

  const showResearch = enabled || status !== "idle" || !!plan;

  const liveConfidence = useMemo(
    () => Math.round((status === "idle" ? confidence : metrics.confidence * 100)),
    [status, confidence, metrics.confidence],
  );
  const liveRisk = useMemo(
    () => Math.round((status === "idle" ? risk : metrics.risk * 100)),
    [status, risk, metrics.risk],
  );

  return (
    <aside className="w-[360px] shrink-0 border-l border-border bg-surface-1/60 backdrop-blur-xl flex flex-col h-full overflow-hidden">
      <div className="px-4 h-12 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          {showResearch ? <FlaskConical className="w-4 h-4 text-secondary" /> : <Sparkles className="w-4 h-4 text-primary" />}
          <span className="text-sm font-display font-semibold">
            {showResearch ? "Deep Research" : "Transparency"}
          </span>
        </div>
        <span className={`text-[10px] font-mono ${status === "idle" ? "text-success" : status === "failed" ? "text-destructive" : "text-primary"}`}>
          ● {status === "idle" ? "LIVE" : status.toUpperCase()}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Risk + Confidence */}
        <div className="grid grid-cols-2 gap-2">
          <Meter label="Confidence" value={liveConfidence} color="success" />
          <Meter label="Risk" value={liveRisk} color="warning" inverse />
        </div>

        {showResearch ? (
          <>
            {/* Live graph */}
            <Section title="Plan × Swarm" icon={Zap}>
              <DeepResearchGraph height={240} />
            </Section>

            {/* Sub-agents */}
            <Section title="Sub-Agents" icon={Sparkles}>
              <SwarmAgentList />
            </Section>

            {/* Current plan checklist (level-1 nodes) */}
            {plan && (
              <Section title="Current Plan" icon={Zap}>
                <ol className="space-y-1.5">
                  {plan.nodes[plan.rootId]?.childIds.map((cid) => {
                    const n = plan.nodes[cid];
                    if (!n) return null;
                    return (
                      <li key={cid} className="flex items-center gap-2 text-xs">
                        <span className={`w-4 h-4 rounded shrink-0 flex items-center justify-center border ${
                          n.status === "done" ? "bg-success/20 border-success/40 text-success" :
                          n.status === "running" ? "bg-primary/20 border-primary/40 text-primary animate-pulse" :
                          n.status === "failed" ? "bg-destructive/20 border-destructive/40 text-destructive" :
                          "bg-surface-2 border-border text-muted-foreground"
                        }`}>
                          {n.status === "done" && <Check className="w-3 h-3" />}
                          {n.status === "running" && <Play className="w-2.5 h-2.5" />}
                        </span>
                        <span className="truncate" title={n.question}>{n.question}</span>
                      </li>
                    );
                  })}
                </ol>
              </Section>
            )}

            {/* Tools galaxy snapshot from agents */}
            <Section title="Tools In Flight" icon={Sparkles}>
              <div className="space-y-1.5">
                {agents.slice(0, 6).map((a) => (
                  <div key={a.id} className="flex items-center gap-2 text-[11px] font-mono p-2 rounded bg-surface-2/60 border border-border">
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      a.status === "done" ? "bg-success" :
                      a.status === "failed" ? "bg-destructive" :
                      "bg-primary animate-pulse"
                    }`} />
                    <span className="flex-1 truncate">{a.toolsActive.join("·") || "—"}</span>
                    <span className="text-muted-foreground">{Math.round(a.latencyMs)}ms</span>
                  </div>
                ))}
                {agents.length === 0 && (
                  <div className="text-[10px] font-mono text-muted-foreground italic">No agent tools running.</div>
                )}
              </div>
            </Section>

            {/* Guardrails */}
            <Section title="Guardrails" icon={ShieldAlert}>
              <div className="grid grid-cols-3 gap-2">
                <NumInput
                  label="tokens" value={guardrails.maxTokens}
                  onChange={(v) => setGuardrails({ maxTokens: v })}
                  step={10000} min={1000}
                />
                <NumInput
                  label="time s" value={guardrails.maxTimeSec}
                  onChange={(v) => setGuardrails({ maxTimeSec: v })}
                  step={10} min={5}
                />
                <NumInput
                  label="$ max" value={guardrails.maxCostUsd}
                  onChange={(v) => setGuardrails({ maxCostUsd: v })}
                  step={0.1} min={0} float
                />
              </div>
              <div className="mt-2 text-[10px] font-mono text-muted-foreground flex items-center gap-2">
                <span>used: {metrics.tokensUsed.toLocaleString()} tok</span>
                <span>·</span>
                <span>${metrics.costUsd.toFixed(3)}</span>
              </div>
            </Section>

            <Section title="Audit Trail" icon={AlertTriangle}>
              <ResearchAuditPanel />
            </Section>
          </>
        ) : (
          // Legacy demo blocks (unchanged behavior, untouched look)
          <>
            <Section title="Current Plan" icon={Zap}>
              <ol className="space-y-1.5">
                {[
                  { t: "Spawn 4 sub-agents", done: true },
                  { t: "Crawl arXiv (last 7d)", done: true },
                  { t: "Embed + cluster claims", done: true },
                  { t: "Adversarial critique", done: false, active: true },
                  { t: "Compose executive brief", done: false },
                  { t: "Human approval gate", done: false, gate: true },
                ].map((s, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs">
                    <span className={`w-4 h-4 rounded shrink-0 flex items-center justify-center border ${
                      s.done ? "bg-success/20 border-success/40 text-success" :
                      s.active ? "bg-primary/20 border-primary/40 text-primary animate-pulse" :
                      "bg-surface-2 border-border text-muted-foreground"
                    }`}>
                      {s.done && <Check className="w-3 h-3" />}
                      {s.active && <Play className="w-2.5 h-2.5" />}
                    </span>
                    <span className={s.done ? "line-through text-muted-foreground" : "text-foreground"}>{s.t}</span>
                    {s.gate && <ShieldAlert className="w-3 h-3 text-warning ml-auto" />}
                  </li>
                ))}
              </ol>
            </Section>

            <Section title="Tool Galaxy" icon={Sparkles}>
              <div className="space-y-1.5">
                {[
                  { name: "arxiv.search", status: "ok", ms: 312, hash: "0xa4f3" },
                  { name: "vector.embed", status: "ok", ms: 184, hash: "0x91cd" },
                  { name: "graph.compose", status: "running", ms: 612, hash: "0x42b1" },
                  { name: "critic.score", status: "queued", ms: 0, hash: "0x77ee" },
                ].map((t, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11px] font-mono p-2 rounded bg-surface-2/60 border border-border">
                    <span className={`status-dot ${
                      t.status === "ok" ? "text-success" :
                      t.status === "running" ? "text-primary animate-pulse" :
                      "text-muted-foreground"
                    }`} />
                    <span className="flex-1 truncate">{t.name}</span>
                    <span className="text-muted-foreground">{t.hash}</span>
                    {t.ms > 0 && <span className="text-muted-foreground">{t.ms}ms</span>}
                  </div>
                ))}
              </div>
            </Section>

            <Section title="Audit Trail" icon={AlertTriangle}>
              <div className="space-y-1 text-[10px] font-mono text-muted-foreground">
                {[
                  "12:42:01 plan.create #4f2a",
                  "12:42:02 tool.exec arxiv.search",
                  "12:42:03 memory.write vec://7c9",
                  "12:42:04 thought.commit 0x91cd",
                  "12:42:05 governance.check pass",
                ].map((l, i) => (
                  <div key={i} className="flex gap-2"><span className="text-success">✓</span><span>{l}</span></div>
                ))}
              </div>
            </Section>
          </>
        )}
      </div>

      {/* Nuclear controls — wired to engine when research active */}
      <div className="border-t border-border p-3 grid grid-cols-2 gap-1.5">
        <Button
          variant="outline" size="sm" className="h-8 text-xs gap-1"
          onClick={() => (isPaused ? resume() : pause())}
          disabled={!showResearch || (!isRunning && !isPaused)}
        >
          {isPaused ? <><Play className="w-3 h-3" /> Resume</> : <><Pause className="w-3 h-3" /> Pause</>}
        </Button>
        <Button
          variant="outline" size="sm" className="h-8 text-xs gap-1"
          onClick={() => rollback()}
          disabled={!showResearch}
        >
          <RotateCcw className="w-3 h-3" /> Rollback
        </Button>
        <Button variant="outline" size="sm" className="h-8 text-xs gap-1 border-secondary/40 text-secondary hover:bg-secondary/10 hover:text-secondary">
          <GitFork className="w-3 h-3" /> Fork
        </Button>
        <Button
          variant="outline" size="sm" className="h-8 text-xs gap-1 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
          onClick={() => stop()}
          disabled={!showResearch || (!isRunning && !isPaused)}
        >
          <X className="w-3 h-3" /> Stop All
        </Button>
        <Button size="sm" className="h-8 text-xs col-span-2 bg-gradient-neural border-0 text-primary-foreground gap-1">
          <Zap className="w-3 h-3" /> Approve & Continue
        </Button>
      </div>
    </aside>
  );
}

function Section({ title, icon: Icon, children }: { title: string; icon: any; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
        <Icon className="w-3 h-3 text-primary" /> {title}
      </div>
      {children}
    </div>
  );
}

function Meter({ label, value, color, inverse }: { label: string; value: number; color: string; inverse?: boolean }) {
  const tone = inverse
    ? value < 25 ? "success" : value < 50 ? "warning" : "destructive"
    : value > 80 ? "success" : value > 50 ? "warning" : "destructive";
  return (
    <div className="rounded-lg p-2.5 bg-surface-2/60 border border-border">
      <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">{label}</div>
      <div className="text-xl font-display font-bold text-foreground">{value}<span className="text-xs text-muted-foreground">%</span></div>
      <div className="mt-1.5 h-1 rounded-full bg-surface-3 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-700 bg-${tone}`}
          style={{ width: `${value}%`, backgroundColor: `hsl(var(--${tone}))` }}
        />
      </div>
    </div>
  );
}

function NumInput({
  label, value, onChange, step, min, float,
}: { label: string; value: number; onChange: (v: number) => void; step: number; min: number; float?: boolean }) {
  return (
    <label className="flex flex-col gap-0.5">
      <span className="text-[9px] font-mono uppercase text-muted-foreground">{label}</span>
      <input
        type="number"
        value={value}
        min={min}
        step={step}
        onChange={(e) => {
          const n = float ? parseFloat(e.target.value) : parseInt(e.target.value, 10);
          if (!Number.isNaN(n)) onChange(n);
        }}
        className="w-full h-7 px-1.5 rounded bg-surface-2 border border-border text-[11px] font-mono outline-none focus:border-primary/60"
      />
    </label>
  );
}
